import { AppPermission } from '@/app.permission';
import { User } from '@/libs/oidc';
import { PBAC } from 'pbac-validator';

/** Realm role trên Keycloak (realm lhv) */
export enum LHVRole {
  Admin = 'lhv_admin',
  PartnerPayment = 'partner_payment',
}

/**
 * Quyền cấp cho từng realm role. Hai role cùng thấy các trang /partner-devices;
 * backend LHV tự giới hạn dữ liệu theo đối tác với role partner_payment.
 */
const LHV_ROLE_PERMISSIONS: Record<LHVRole, AppPermission[]> = {
  [LHVRole.Admin]: [
    AppPermission.LHVPartnerView,
    AppPermission.LHVPartnerReset,
  ],
  [LHVRole.PartnerPayment]: [
    AppPermission.LHVPartnerView,
    AppPermission.LHVPartnerReset,
  ],
};

type LHVTokenClaims = {
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  realm_access?: { roles?: string[] };
};

/** Giải mã phần payload của JWT (không kiểm chữ ký — chỉ để hiển thị/phân quyền UI) */
const decodeJwtPayload = (token?: string): LHVTokenClaims => {
  try {
    const payload = token?.split('.')[1];
    if (!payload) return {};
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(json);
  } catch (error) {
    return {};
  }
};

/**
 * Dựng API.User từ access token OIDC thay cho API v3 /api/v1/user/me
 * (backend v3 không nhận token realm lhv).
 * Không có role nào hợp lệ → pbacStatements rỗng → trang 403.
 */
export const buildLHVUser = (session: User): API.User => {
  const claims = decodeJwtPayload(session.access_token);
  const roles = claims.realm_access?.roles ?? [];

  const granted = new Set<AppPermission>();
  Object.values(LHVRole)
    .filter((role) => roles.includes(role))
    .forEach((role) =>
      LHV_ROLE_PERMISSIONS[role].forEach((p) => granted.add(p)),
    );

  const pbac = new PBAC();
  pbac.addFromFunctionMatrix(
    [...new Set(Object.values(LHV_ROLE_PERMISSIONS).flat())].map((p) => ({
      resource: p.split('.')[0],
      action: p.split('.')[1],
      allow: granted.has(p),
    })),
  );

  const profile = session.profile ?? {};
  const userName =
    claims.preferred_username ??
    (profile.preferred_username as string | undefined) ??
    claims.email ??
    '';

  return {
    id: claims.sub ?? profile.sub ?? '',
    name: claims.name ?? (profile.name as string | undefined) ?? userName,
    userName,
    pbacStatements: pbac.statements,
    role: roles.includes(LHVRole.Admin) ? 'admin' : 'partner',
    active: granted.size > 0,
    terminalIDs: [],
  };
};
